import { useState, useMemo } from 'react';
import {
    Area,
    YAxis,
    XAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    CartesianGrid,
    ComposedChart
} from 'recharts';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { IndexData, MarketDateFilter } from 'models/types';
import { IndexYTick, TimeseriesXTick } from './Ticks';
import { IndexTooltip } from './Tooltips';
import { MarketDateFilters } from './Filters';
import { gradientOffset, getXAxisTicksAmountForTimeChart } from 'utils/chart';
import { filterDataByMarketDateFilter } from 'utils/index';

export interface IndexChartCoreProps {
    indexData: IndexData[];
    showFilters?: boolean;
    showYAxis?: boolean;
    showXAxis?: boolean;
    chartId?: string;
    onFilterChange?: (filter: MarketDateFilter) => void;
}

export default function IndexChartCore({
    indexData,
    onFilterChange,
    showFilters = true,
    showXAxis = true,
    showYAxis = true,
    chartId = '',
}: IndexChartCoreProps) {
    const [filter, setFilter] = useState<MarketDateFilter>(MarketDateFilter.MAX);
    const isMobile = useBreakpointValue({ base: true, md: false });
    const gradientId = `splitColor_${chartId}`;

    const { filteredData } = useMemo(
        () => {
            const filteredData = filterDataByMarketDateFilter(filter, indexData);
            return { filteredData }
        }, [filter, indexData]
    );

    const { off, fullRed } = useMemo(() => {
        const off = gradientOffset(filteredData, 'weightedIndex', 0);
        const startPoint = filteredData[0]?.weightedIndex ?? 0;
        const endPoint = filteredData[filteredData.length - 1]?.weightedIndex ?? 0;
        const fullRed = startPoint > endPoint;
        return { off, fullRed };
    }, [filteredData]);

    const onMarketDateFilterSelect = (filter: MarketDateFilter) => {
        setFilter(filter);
        onFilterChange?.(filter);
    }

    const renderXAxis = () => {
        const totalTicks = getXAxisTicksAmountForTimeChart(!!isMobile, filter);
        const dataLength = filteredData.length;

        let interval = Math.ceil(dataLength / totalTicks);

        if (showXAxis) {
            return (
                <XAxis tick={(props) => <TimeseriesXTick {...props} data={filteredData} />} interval={interval} />
            );
        }
    };

    const renderYAxis = () => {
        if (showYAxis) {
            return (
                <YAxis
                    tick={IndexYTick}
                />
            );
        }
    }

    const renderChartData = () => {
        return (
            <ComposedChart data={filteredData}>
                <CartesianGrid horizontal vertical={false} />
                {renderYAxis()}
                {renderXAxis()}
                <Tooltip content={(props) => <IndexTooltip indexPriceData={filteredData} {...props} />} />
                <Area
                    type="monotone"
                    dataKey="weightedIndex"
                    stroke="none"
                    fill={`url(#${gradientId})`}
                />
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset={off} stopColor={fullRed ? "red" : "green"} stopOpacity={1} />
                        {!fullRed && <stop offset={off} stopColor="red" stopOpacity={1} />}
                    </linearGradient>
                </defs>
                <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" />
            </ComposedChart>
        );
    };

    const renderFilters = () => {
        if (showFilters) {
            return (
                <Box paddingLeft={4}>
                    <MarketDateFilters selectedFilter={filter} onFilterSelect={onMarketDateFilterSelect} />
                </Box>
            )
        }
    }

    return (
        <Flex w="100%" height={showFilters ? 400 : 300} mt={4} alignItems={'flex-start'} direction={'column'}>
            {renderFilters()}
            <ResponsiveContainer>{renderChartData()}</ResponsiveContainer>
        </Flex>
    );
}
